import { LogOut, FolderOpen, Settings, ChevronDown } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useAuth } from "./AuthProvider";

type Props = {
  onSettingsClick?: () => void;
  kbUrl?: string | null;
};

export default function UserMenu({ onSettingsClick, kbUrl }: Props) {
  const user = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close menu when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  const handleSettingsClick = () => {
    setIsOpen(false);
    onSettingsClick?.();
  };

  if (!user) return null;

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 rounded-md px-3 py-2 text-sm text-gray-300 transition-colors hover:bg-gray-800 hover:text-white"
        title="User menu"
      >
        <span>{user.name}</span>
        <ChevronDown 
          className="h-4 w-4 transition-transform duration-200"
          style={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)' }}
        />
      </button>

      <div
        className={`absolute right-0 mt-1 w-48 rounded-lg border border-gray-700 bg-gray-900 shadow-lg z-50 transition-opacity duration-100 ${isOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
      >
          {kbUrl && (
            <a
              href={kbUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setIsOpen(false)}
              className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-300 transition-colors hover:bg-gray-800 hover:text-white border-b border-gray-700"
            >
              <FolderOpen className="h-4 w-4" />
              <span>AI Notes (OneDrive)</span>
            </a>
          )}
          <button
            onClick={handleSettingsClick}
            className="w-full flex items-center gap-2.5 px-4 py-3 text-sm text-gray-300 transition-colors hover:bg-gray-800 hover:text-white border-b border-gray-700 text-left"
          >
            <Settings className="h-4 w-4" />
            <span>Settings</span>
          </button>
          <a
            href="/api/auth/logout"
            title="Sign out"
            className="flex items-center gap-2.5 px-4 py-3 text-sm text-gray-400 transition-colors hover:bg-gray-800 hover:text-red-400"
          >
            <LogOut className="h-4 w-4" />
            <span>Sign out</span>
          </a>
        </div>
    </div>
  );
}
